import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Title from "../common/Title";
import { TbEyeSearch } from "react-icons/tb";
import { Button, Form, Row, Col, Collapse } from "react-bootstrap";
import { FaAngleDown, FaAngleUp, FaCopy, FaSync, FaTimes } from "react-icons/fa";
import EnhancedGrid from "../common/EnhancedGrid";
import { inspectRequestActions } from "../../actions/inspectRequest.actions";
import moment from "moment";
import { JsonView } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";

function InspectRequestGrid({ hideTitle, user }) {
  const dispatch = useDispatch();
  const inspectRequestState = useSelector((state) => state.inspectRequest);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [runbookIdFilter, setRunbookIdFilter] = useState("");
  // Defaults to whatever client is selected in the top menu, same as every other
  // client-scoped admin grid (Audit Log, Variables, Connectors, ...) — without this it
  // silently showed every client's captured requests regardless of the selector. Still a
  // plain text filter underneath so Product Team can clear it and look across clients, or
  // type a different one, same as before.
  const [clientIdFilter, setClientIdFilter] = useState(user?.clientId || "");
  const [copied, setCopied] = useState(false);

  const handleCopyPayload = (payload) => {
    navigator.clipboard.writeText(payload);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchRequests = (overrideClientIdFilter) => {
    // Omit runbookId/clientId entirely when empty rather than setting them to undefined -
    // Utils.objectToQueryString serializes an explicit undefined as the literal string
    // "undefined" (encodeURIComponent(undefined) === "undefined", which is truthy), so an
    // omitted key is the only way to send an actually-unfiltered request.
    const effectiveClientIdFilter = overrideClientIdFilter ?? clientIdFilter;
    const params = { max_results: 200 };
    if (runbookIdFilter) params.runbookId = runbookIdFilter;
    if (effectiveClientIdFilter) params.clientId = effectiveClientIdFilter;

    dispatch(inspectRequestActions.get(params));
  };

  // Rows auto-purge nightly (24h retention), so a fixed page of 200 recent
  // rows covers the feature without needing URL-based server paging.
  useEffect(() => {
    setClientIdFilter(user?.clientId || "");
    fetchRequests(user?.clientId || "");
    // eslint-disable-next-line
  }, [user?.clientId]);

  const handleClearFilters = () => {
    setRunbookIdFilter("");
    setClientIdFilter("");
  };

  const rows = inspectRequestState.paginatedResult?.data || [];

  const data = rows.map((r) => ({
    ...r,
    createdOn: r.createdOn ? moment(r.createdOn + "Z").format("MM/DD/YYYY h:mm A") : "-",
  }));

  // Render as a collapsible tree when it's valid JSON (the normal case); anything that
  // doesn't parse (a future encrypted envelope, malformed capture, etc.) falls back to plain
  // text rather than silently showing nothing.
  const parsedPayload = useMemo(() => {
    if (!selectedRequest?.requestPayload) return undefined;
    try {
      return JSON.parse(selectedRequest.requestPayload);
    } catch {
      return null;
    }
  }, [selectedRequest]);

  return (
    <>
      {!hideTitle && <Title text="Inspect Requests" icon={<TbEyeSearch className="mb-1" />} />}

      {/* Rows are fetched once on load (and again if the client selector changes) — nothing
          here polls, so a request captured after that needs an explicit refresh to show up. */}
      <div className="d-flex justify-content-end mb-2">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => fetchRequests()}
          disabled={inspectRequestState.loading}
        >
          <FaSync className={`me-1 ${inspectRequestState.loading ? "fa-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="bg-light border p-3 mb-3 rounded">
        <Collapse in={filterExpanded}>
          <div>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Runbook ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={runbookIdFilter}
                    onChange={(e) => setRunbookIdFilter(e.target.value)}
                    placeholder="Filter by runbook ID"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Client ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={clientIdFilter}
                    onChange={(e) => setClientIdFilter(e.target.value)}
                    placeholder="Filter by client ID"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Button
              variant="link"
              size="sm"
              className="text-decoration-none p-0 m-0 float-start fw-bold"
              onClick={() => setFilterExpanded(false)}
            >
              Click here to collapse filters <FaAngleUp size={13} />
            </Button>
            <Button variant="primary" className="float-end ms-2" size="sm" onClick={fetchRequests}>
              Apply filters
            </Button>
            <Button
              variant="secondary"
              className="float-end"
              size="sm"
              onClick={() => {
                handleClearFilters();
                dispatch(inspectRequestActions.get({ max_results: 200 }));
              }}
            >
              Clear filters
            </Button>
            <div className="clearfix" />
          </div>
        </Collapse>
        {!filterExpanded && (
          <Button
            variant="link"
            size="sm"
            className="text-decoration-none p-0 m-0 fw-bold"
            onClick={() => setFilterExpanded(true)}
          >
            Click here to expand filters <FaAngleDown size={13} />
          </Button>
        )}
      </div>

      <EnhancedGrid
        gridId="inspectrequest"
        fillHeight={hideTitle}
        emptyState={{
          icon: <TbEyeSearch size={80} className="text-muted mb-3" />,
          message: "No inspect requests found",
          subtitle: "Rows appear here when a runbook step with Inspect Request enabled runs.",
        }}
        data={data}
        displayColumns={["createdOn", "runbookId", "executionId", "clientId", "stepName"]}
        idColumn={"inspectRequestId"}
        selectedRow={selectedRequest}
        onRowClick={(row) =>
          setSelectedRequest((current) =>
            current?.inspectRequestId === row.inspectRequestId ? null : row
          )
        }
        pageNumber={0}
        totalPages={rows.length > 0 ? 1 : 0}
        loading={inspectRequestState.loading}
      />

      {selectedRequest && (
        <div className="mt-3 p-3 bg-light rounded">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <h5 className="mb-0">Request Payload</h5>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handleCopyPayload(selectedRequest.requestPayload)}
              >
                <FaCopy className="me-1" />
                {copied ? "Copied!" : "Copy"}
              </Button>
              <Button
                variant="link"
                size="sm"
                className="text-decoration-none p-0"
                onClick={() => setSelectedRequest(null)}
              >
                <FaTimes className="me-1" />
                Close
              </Button>
            </div>
          </div>
          {parsedPayload !== null && parsedPayload !== undefined ? (
            <div className="bg-white p-2 rounded" style={{ maxHeight: "250px", overflow: "auto", fontSize: "12px" }}>
              <JsonView data={parsedPayload} shouldExpandNode={() => true} />
            </div>
          ) : (
            <pre
              className="bg-white p-2 rounded"
              style={{
                fontSize: "12px",
                maxHeight: "250px",
                overflow: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {selectedRequest.requestPayload}
            </pre>
          )}
        </div>
      )}
    </>
  );
}

export default InspectRequestGrid;
